import { useCallback, useEffect, useRef, useState } from 'react';
import { TILE_LIFT_PRESS_MS, TILE_PRESS_SLOP, tileAtPoint } from '../../tileOrder';
/**
 * Drag and drop of the other people's tiles on the web call stage
 * (VRSLayout.tsx, tileOrder.ts). Robert, 2026-10-07 ("all tiles can switch
 * with any other tile just by dragging and dropping").
 *
 * - Mouse and pen: a press of the primary button lifts the tile when the
 *   pointer moves more than TILE_DRAG_START_PX.
 * - Touch: a press of TILE_LIFT_PRESS_MS that moves no more than
 *   TILE_PRESS_SLOP lifts the tile. A shorter touch stays a tap, and the tap
 *   still shows or hides the call bar.
 * - A drop on another tile swaps the two. A drop in a different place,
 *   Escape and a cancelled pointer put the tile back.
 * - The outline under the pointer moves by direct style writes. React
 *   renders again only when the drag starts, when the tile under the pointer
 *   changes, and when the drag ends.
 */
/** A mouse or a pen lifts a tile after this distance (as the self view: 6 px). */
export const TILE_DRAG_START_PX = 6;
/** A press on these keeps its own meaning: it never lifts the tile. */
export const NO_DRAG_SELECTOR = 'button, select, input, a, [data-vrs-no-drag]';
/** The tiles that can move and take a drop. */
export const SWAPPABLE_TILE_SELECTOR = '[data-vrs-swappable]';
/** Your own video: never a drop target, also where it floats over a tile. */
export const SELF_VIEW_SELECTOR = '[data-self-view="true"]';
/**
 * Whether a press on this element can lift its tile. A press on a control
 * (the fit button, the swap menu) keeps its own meaning.
 *
 * @param {EventTarget|null} target - The element under the pointer.
 * @returns {boolean}
 */
export function canStartTileDrag(target) {
    const element = target;
    return !(typeof element?.closest === 'function' && element.closest(NO_DRAG_SELECTOR));
}
/**
 * Whether a press lifts its tile now.
 *
 * @param {Object} press - The press so far.
 * @param {number} press.distance - How far the pointer moved, in pixels.
 * @param {number} press.elapsedMs - How long the press is.
 * @param {string} press.pointerType - Mouse, pen or touch.
 * @returns {boolean}
 */
export function shouldStartTileDrag({ distance, elapsedMs, pointerType }) {
    if (pointerType === 'touch') {
        return elapsedMs >= TILE_LIFT_PRESS_MS && distance <= TILE_PRESS_SLOP;
    }
    return distance > TILE_DRAG_START_PX;
}
/**
 * Whether a press stops before it lifts its tile: a finger that moves too
 * far before the press is long enough.
 *
 * @param {Object} press - The press so far.
 * @param {number} press.distance - How far the pointer moved, in pixels.
 * @param {string} press.pointerType - Mouse, pen or touch.
 * @returns {boolean}
 */
export function abandonsTilePress({ distance, pointerType }) {
    return pointerType === 'touch' && distance > TILE_PRESS_SLOP;
}
/**
 * The tile that takes a drop at a point: another tile that can move. A point
 * on your own video takes no drop, also where it floats over a tile.
 *
 * @param {Object} drop - The drop.
 * @param {Map} [drop.blocked] - The places that take no drop.
 * @param {string} drop.from - The lifted tile.
 * @param {Object} drop.point - The pointer.
 * @param {Map} drop.rects - The tiles that can take a drop.
 * @returns {string|undefined}
 */
export function tileDropTarget({ blocked, from, point, rects }) {
    if (blocked && tileAtPoint(blocked, point)) {
        return undefined;
    }
    return tileAtPoint(rects, point, from);
}
/**
 * The transform that puts the outline of the lifted tile under the pointer.
 * The outline keeps the place in the tile where the press started.
 *
 * @param {Object} move - The move.
 * @param {Object} move.origin - Where the outline is with no transform (its
 * containing block: the window, or an ancestor with a transform).
 * @param {Object} move.point - The pointer now.
 * @param {ITileRect} move.source - The lifted tile.
 * @param {Object} move.start - The pointer at the press.
 * @returns {string}
 */
export function ghostTransform({ origin, point, source, start }) {
    const x = source.x - origin.x + point.x - start.x;
    const y = source.y - origin.y + point.y - start.y;
    return `translate3d(${x}px, ${y}px, 0)`;
}
/**
 * The place of each tile that matches a selector, by its pane key.
 *
 * @param {ParentNode|null|undefined} root - The stage.
 * @param {string} selector - The tiles to measure.
 * @param {string[]} [keys] - Only these tiles.
 * @returns {Map}
 */
export function readTileRects(root, selector, keys) {
    const rects = new Map();
    root?.querySelectorAll(selector).forEach(element => {
        const key = element.getAttribute('data-vrs-pane');
        if (key && (!keys || keys.includes(key))) {
            const box = element.getBoundingClientRect();
            rects.set(key, {
                height: box.height,
                width: box.width,
                x: box.left,
                y: box.top
            });
        }
    });
    return rects;
}
/**
 * Stops the click that follows a drop: a drag is not a click on the stage.
 *
 * @returns {void}
 */
function swallowNextClick() {
    const swallow = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };
    window.addEventListener('click', swallow, {
        capture: true,
        once: true
    });
    // The click of a pointerup comes in the same task. Later clicks are real.
    window.setTimeout(() => window.removeEventListener('click', swallow, true), 0);
}
/**
 * Drag and drop of the tiles on the web call stage.
 *
 * @param {IOptions} options - Whether swaps are offered, the tiles that can
 * move, what a drop does, and the stage.
 * @returns {Object} The drag now, the ref of its outline, and the pointer
 * props of each tile that can move.
 */
export default function useTileDrag({ enabled, keys, onSwap, stage }) {
    const [drag, setDrag] = useState(undefined);
    const press = useRef(null);
    const ghost = useRef(null);
    const origin = useRef({
        x: 0,
        y: 0
    });
    // The window listeners live for one press. They read the newest props.
    const latest = useRef({
        keys,
        onSwap,
        stage
    });
    latest.current = {
        keys,
        onSwap,
        stage
    };
    // One controller for the life of the stage: the window listeners of a press
    // must be the same functions when they are removed.
    const [controller] = useState(() => {
        /**
         * Moves the outline under the pointer, with no render.
         *
         * @returns {void}
         */
        function place() {
            const current = press.current;
            const node = ghost.current;
            if (!current?.source || !node) {
                return;
            }
            node.style.transform = ghostTransform({
                origin: origin.current,
                point: current.last,
                source: current.source,
                start: current.start
            });
        }
        /**
         * The tile under the pointer that takes a drop.
         *
         * @param {IPress} current - The press.
         * @returns {string|undefined}
         */
        function targetOf(current) {
            return current.rects && !current.cancelled
                ? tileDropTarget({
                    blocked: current.blocked,
                    from: current.key,
                    point: current.last,
                    rects: current.rects
                })
                : undefined;
        }
        /**
         * Ends the press: no more listeners, no capture, no outline.
         *
         * @param {boolean} [render] - False on unmount.
         * @returns {void}
         */
        function end(render = true) {
            const current = press.current;
            if (!current) {
                return;
            }
            press.current = null;
            current.timer !== undefined && window.clearTimeout(current.timer);
            window.removeEventListener('pointermove', onMove, true);
            window.removeEventListener('pointerup', onUp, true);
            window.removeEventListener('pointercancel', onPointerCancel, true);
            window.removeEventListener('keydown', onKeyDown, true);
            window.removeEventListener('blur', onBlur);
            try {
                if (current.element.hasPointerCapture?.(current.pointerId)) {
                    current.element.releasePointerCapture(current.pointerId);
                }
            }
            catch {
                // The pointer is gone already.
            }
            current.lifted && render && setDrag(undefined);
        }
        /**
         * Lifts the tile: measures the tiles once and shows the outline.
         *
         * @param {IPress} current - The press.
         * @returns {void}
         */
        function lift(current) {
            const root = latest.current.stage.current;
            const rects = readTileRects(root, SWAPPABLE_TILE_SELECTOR, latest.current.keys);
            const source = rects.get(current.key);
            if (!source) {
                end();
                return;
            }
            current.lifted = true;
            current.rects = rects;
            current.source = source;
            current.blocked = readTileRects(root, SELF_VIEW_SELECTOR);
            current.target = targetOf(current);
            try {
                current.element.setPointerCapture(current.pointerId);
            }
            catch {
                // The pointer is gone: pointercancel or pointerup follows.
            }
            window.addEventListener('keydown', onKeyDown, true);
            setDrag({
                height: source.height,
                key: current.key,
                target: current.target,
                width: source.width
            });
        }
        /**
         * Starts a drag, or moves the outline and finds the tile under it.
         *
         * @param {PointerEvent} event - The move.
         * @returns {void}
         */
        function onMove(event) {
            const current = press.current;
            if (!current || event.pointerId !== current.pointerId) {
                return;
            }
            current.last = {
                x: event.clientX,
                y: event.clientY
            };
            if (current.cancelled) {
                return;
            }
            if (!current.lifted) {
                const distance = Math.hypot(current.last.x - current.start.x, current.last.y - current.start.y);
                // A button that was let go outside the window sends no
                // pointerup here. The lowest bit of `buttons` is the primary
                // button.
                if ((current.pointerType !== 'touch' && event.buttons % 2 === 0)
                    || abandonsTilePress({
                        distance,
                        pointerType: current.pointerType
                    })) {
                    end();
                }
                else if (shouldStartTileDrag({
                    distance,
                    elapsedMs: Date.now() - current.startedAt,
                    pointerType: current.pointerType
                })) {
                    lift(current);
                }
                return;
            }
            place();
            const target = targetOf(current);
            if (target !== current.target) {
                current.target = target;
                setDrag(now => {
                    if (!now) {
                        return now;
                    }
                    return {
                        ...now,
                        target
                    };
                });
            }
        }
        /**
         * A drop: on another tile the two swap. Any other drop puts the
         * tile back.
         *
         * @param {PointerEvent} event - The pointerup.
         * @returns {void}
         */
        function onUp(event) {
            const current = press.current;
            if (!current || event.pointerId !== current.pointerId) {
                return;
            }
            current.last = {
                x: event.clientX,
                y: event.clientY
            };
            const target = current.lifted ? targetOf(current) : undefined;
            end();
            current.lifted && swallowNextClick();
            target && latest.current.onSwap(current.key, target);
        }
        /**
         * The browser took the pointer: the tile goes back.
         *
         * @param {PointerEvent} event - The pointercancel.
         * @returns {void}
         */
        function onPointerCancel(event) {
            event.pointerId === press.current?.pointerId && end();
        }
        /**
         * The window lost the focus: the tile goes back.
         *
         * @returns {void}
         */
        function onBlur() {
            end();
        }
        /**
         * Escape puts the lifted tile back. The press stays until pointerup,
         * so that its click does not reach the stage.
         *
         * @param {KeyboardEvent} event - The key.
         * @returns {void}
         */
        function onKeyDown(event) {
            const current = press.current;
            if (event.key !== 'Escape' || !current?.lifted || current.cancelled) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            current.cancelled = true;
            current.target = undefined;
            window.removeEventListener('keydown', onKeyDown, true);
            setDrag(undefined);
        }
        /**
         * A press on a tile. A touch press starts its timer.
         *
         * @param {string} key - The tile.
         * @param {PointerEvent} event - The pointerdown.
         * @returns {void}
         */
        function onPointerDown(key, event) {
            if (press.current || !event.isPrimary
                || (event.pointerType !== 'touch' && event.button !== 0)
                || !canStartTileDrag(event.target)) {
                return;
            }
            const point = {
                x: event.clientX,
                y: event.clientY
            };
            const current = {
                cancelled: false,
                element: event.currentTarget,
                key,
                last: point,
                lifted: false,
                pointerId: event.pointerId,
                pointerType: event.pointerType,
                start: point,
                startedAt: Date.now()
            };
            press.current = current;
            if (current.pointerType === 'touch') {
                current.timer = window.setTimeout(() => {
                    current.timer = undefined;
                    if (press.current === current && !current.lifted && shouldStartTileDrag({
                        distance: Math.hypot(current.last.x - current.start.x, current.last.y - current.start.y),
                        elapsedMs: Date.now() - current.startedAt,
                        pointerType: current.pointerType
                    })) {
                        lift(current);
                    }
                }, TILE_LIFT_PRESS_MS);
            }
            window.addEventListener('pointermove', onMove, true);
            window.addEventListener('pointerup', onUp, true);
            window.addEventListener('pointercancel', onPointerCancel, true);
            window.addEventListener('blur', onBlur);
        }
        /**
         * A long touch press opens no context menu on a tile.
         *
         * @param {MouseEvent} event - The contextmenu event.
         * @returns {void}
         */
        function onContextMenu(event) {
            press.current?.pointerType === 'touch' && event.preventDefault();
        }
        /**
         * A picture on a tile (a profile photo) does not start the drag of
         * the browser, which cancels the pointer.
         *
         * @param {DragEvent} event - The dragstart event.
         * @returns {void}
         */
        function onDragStart(event) {
            event.preventDefault();
        }
        return {
            end,
            onContextMenu,
            onDragStart,
            onPointerDown,
            place
        };
    });
    // The lifted tile left the stage (the person left the call), or swaps
    // stopped (one other person is left): the drag stops.
    const keysSignature = keys.join('|');
    useEffect(() => {
        const current = press.current;
        if (current && (!enabled || !latest.current.keys.includes(current.key))) {
            controller.end();
        }
    }, [controller, enabled, keysSignature]);
    useEffect(() => () => controller.end(false), [controller]);
    // The outline mounts when the drag starts. Its place with no transform
    // is the corner of its containing block: the window, or an ancestor with
    // a transform (#videoconference_page has one).
    const ghostRef = useCallback((node) => {
        ghost.current = node;
        if (!node) {
            return;
        }
        node.style.transform = '';
        const box = node.getBoundingClientRect();
        origin.current = {
            x: box.left,
            y: box.top
        };
        controller.place();
    }, [controller]);
    const tileProps = (key) => {
        return {
            onContextMenu: controller.onContextMenu,
            onDragStart: controller.onDragStart,
            onPointerDown: (event) => controller.onPointerDown(key, event)
        };
    };
    return {
        drag,
        ghostRef,
        tileProps
    };
}
